import axios from 'axios';
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { AlertContext, trackerState } from '../App';
import { EditMyInfoCompleteModal } from './modal/EditMyInfoCompleteModal';
import { EditMyInfoProps, UserInfo } from '.';
import { Header } from './Header';
import { LabelInput } from './LabelInput';
import { ModalPortal } from '../Portal';
import { getErrorMessage } from '../util/message';

import style from './EditMyInfoComponents.module.scss';
import { useTranslation } from 'react-i18next';

interface EditMyInfoAuthProps {
  accessToken: string;
  phoneNumber?: string;
  userInfo: UserInfo;
  onComplete(): void;
  onFailed(context: string): void;
}

const DURATION = 60 * 3; // 3분 타이머
const EditMyInfoAuth: React.FC<EditMyInfoAuthProps> = (props: EditMyInfoAuthProps) => {
  const { t } = useTranslation();
  const { accessToken, phoneNumber: phoneNumberString, userInfo, onComplete, onFailed } = props;
  const [ inputValue, setInputValue ] = useState('');
  const [ error, setError ] = useState(false);
  const [ disabled, setDisable ] = useState(true);
  const [ tick, setTick ] = useState(0);
  const config = {
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: { Authorization: `Bearer ${accessToken}` },
  };
  const handleReStartTimer = async () => {
    if (!phoneNumberString) {
      return;
    }
    const phoneNumber = parsePhoneNumberFromString(phoneNumberString, userInfo.country);
    if (!phoneNumber) {
      return;
    }

    trackEvent('휴대폰번호 인증번호', '재발송', '휴대폰번호 인증번호 재발송 버튼 클릭');

    try {
      const data = { phone_number: phoneNumber.number };
      await axios.post(`/mobile/users/${userInfo.uuid}/request-phone-authkey/`, data, config);
    } catch (err) {
      onFailed(getErrorMessage(err));
    }
    startTimer(DURATION);
  };
  const intervalId = useRef<number | null>(null);
  const stopTimer = useCallback(() => {
    if (intervalId.current != null) {
      window.clearInterval(intervalId.current);
      intervalId.current = null;
    }
  }, [intervalId]);
  const startTimer = useCallback((duration: number) => {
    let timer = new Date();
    timer.setSeconds(timer.getSeconds() + duration);
    setTick(duration);
    setError(false);
    const id = window.setInterval(() => {
      let diff = timer.getTime() - Date.now();
      setTick(diff);

      if (diff <= 0) {
        stopTimer();
        onFailed(t('시간이 초과되었습니다. 재발송 버튼을 눌러 인증 번호를 재발급하세요.'));
        setError(true);
      }
    }, 200);
    intervalId.current = id;
  }, [intervalId, onFailed, stopTimer, t]);
  const tracker = useContext(trackerState);
  const trackEvent = (category: string, action: string, label: string) => {
    tracker && tracker.trackEvent(category, action, label);
  };
  const handleFocusAuth = () => {
    trackEvent('휴대폰번호 인증번호', '입력', '휴대폰번호 인증번호 입력폼 클릭');
  };
  const handleOnClick = async () => {
    const phoneNumber = phoneNumberString ?
            parsePhoneNumberFromString(phoneNumberString, userInfo.country) : undefined;

    let valid = inputValue.length === 4;

    if (valid && phoneNumber != null) {
      const payload = {
        phone_number: phoneNumber.number,
        auth_key: inputValue,
      };
      try {
        const resp = await axios.put(
                    `/mobile/users/${userInfo.uuid}/phone-account/`,
                    payload,
                    config,
                );
        if (resp.data.result === 'error') {
          onFailed(getErrorMessage(resp));
        } else {
          onComplete();
        }
      } catch (err: any) {
        onFailed(getErrorMessage(err));
        if (err.response && err.response.data && err.response.data.reason === 'count_limit_exceeded') {
          stopTimer();
          setTick(0);
          setError(true);
        }
        return;
      }
    }

    setError(!valid);
    setDisable(valid);
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLDataElement>) => {
    const val = e.currentTarget.value;
    const valid = val.length < 4;
    setInputValue(val.slice(0, 4));
    setDisable(valid);
  };
  const tickSeconds = Math.floor(tick / 1000);
  const sec = tickSeconds % 60;
  const min = (tickSeconds - sec) / 60;
  const timer = (
        <span className={style.time}>
            {t('timer', { min: min.toString().padStart(2, '0'), sec: sec.toString().padStart(2, '0') })}
        </span>
    );
  const timeout = (
        <button className={style.resend} onClick={handleReStartTimer}>
            {t('재발송')}
        </button>
    );

  useEffect(() => {
    startTimer(DURATION);
    return () => stopTimer();
  }, [startTimer, stopTimer]);

  return (
        <>
            <div className={classNames(style.cards, { [style.invalid]: error })}>
                <LabelInput
                    type="number"
                    label={t('인증번호')}
                    placeholder="1234"
                    value={inputValue}
                    error={error}
                    onChange={onChangeHandler}
                    onFocus={handleFocusAuth}
                >
                    {tick <= 0 ? timeout : timer}
                </LabelInput>
            </div>
            <div className={style.confirm}>
                <button
                    className="btn"
                    disabled={disabled}
                    onClick={handleOnClick}
                >
                    {t('확인')}
                </button>
            </div>
        </>
  );
};

type EditAuthProps = EditMyInfoProps;

const EditAuth: React.FC<EditAuthProps> = (props: EditAuthProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken, userInfo } = props;
  const [ complete, setComplete ] = useState(false);
  const alert = useContext(AlertContext);
  const handleFailure = useCallback((message: string) => {
    alert({
      content: message,
      type: 'error',
    });
  }, [alert]);
  const handleModalClose = () => navigate('/profile', { state: location.state });

  return (
        <>
            {complete &&
                <ModalPortal>
                    <EditMyInfoCompleteModal onDismiss={handleModalClose} />
                </ModalPortal>}
            <Header title={t('인증번호 4자리를 입력하세요.')} />
            <EditMyInfoAuth
                accessToken={accessToken}
                phoneNumber={userInfo.phoneNumber}
                userInfo={userInfo}
                onComplete={() => setComplete(true)}
                onFailed={handleFailure}
            />
        </>
  );
};

export default EditAuth;
